/**
 * Confirmation state machine.
 *
 * The transition table is the contract. The API enforces it; the UI reads it to
 * decide which actions to offer. Neither can drift from the other because both
 * import this table.
 *
 * Authority still rests entirely with the server: the UI offering an action is
 * a hint, and the server re-evaluates every guard against the loaded entity
 * before writing anything (CONF-005, STAT-002).
 *
 * Spec §1 defines DRAFT → SENT → RECEIVED → READ → REPLIED → RESOLVED → CLOSED.
 * Transitions marked `proposal` are gaps G-01…G-03 — present in the table but
 * gated by a feature flag until approved. See docs/04-DECISION-LOG.md.
 */

import { z } from 'zod';
import type { ConfirmationStatus } from './enums';
import type { Permission } from './permissions';

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export const CONFIRMATION_ACTIONS = [
  'create',
  'send',
  'deliver',
  'open',
  'reply',
  'followUp',
  'resolve',
  'close',
  'reopen',
  'cancel',
  'discardDraft',
  'reassign',
] as const;

export const confirmationActionSchema = z.enum(CONFIRMATION_ACTIONS);
export type ConfirmationAction = z.infer<typeof confirmationActionSchema>;

/** Which side of the correspondence may invoke an action. */
export const ACTOR_SIDES = ['ORIGIN', 'RECIPIENT', 'EITHER', 'SYSTEM'] as const;
export type ActorSide = (typeof ACTOR_SIDES)[number];

export interface TransitionDefinition {
  readonly action: ConfirmationAction;
  /** Statuses this action may be invoked from. Empty means "no prior state". */
  readonly from: readonly ConfirmationStatus[];
  /** Resulting status. `null` means the record leaves the lifecycle entirely. */
  readonly to: ConfirmationStatus | null;
  readonly permission: Permission | null;
  readonly actorSide: ActorSide;
  /** Lifecycle timestamps this transition stamps, in write order. */
  readonly stamps: readonly string[];
  /** A free-text reason must accompany the request. */
  readonly requiresReason: boolean;
  /** Proposal awaiting approval — gated behind a feature flag. */
  readonly proposal: boolean;
  readonly description: string;
}

// ---------------------------------------------------------------------------
// Transition table
// ---------------------------------------------------------------------------

export const TRANSITIONS: readonly TransitionDefinition[] = [
  {
    action: 'create',
    from: [],
    to: 'DRAFT',
    permission: 'confirmation.create',
    actorSide: 'ORIGIN',
    stamps: [],
    requiresReason: false,
    proposal: false,
    description: 'Author creates a private draft. No reference number is allocated yet.',
  },
  {
    action: 'send',
    from: ['DRAFT'],
    to: 'SENT',
    permission: 'confirmation.send',
    actorSide: 'ORIGIN',
    stamps: ['sentAt'],
    requiresReason: false,
    proposal: false,
    description:
      'Allocates the reference number and materialises recipients, participants, ' +
      'the initial message and the SLA record — in one transaction.',
  },
  {
    action: 'deliver',
    from: ['SENT'],
    to: 'RECEIVED',
    permission: null,
    actorSide: 'SYSTEM',
    stamps: ['receivedAt'],
    requiresReason: false,
    proposal: false,
    description:
      'Delivery worker acknowledges the confirmation into the recipient inbox ' +
      'and dispatches notifications. Makes delivery time a real measure (ASM-004).',
  },
  {
    action: 'open',
    from: ['RECEIVED'],
    to: 'READ',
    permission: 'confirmation.read',
    actorSide: 'RECIPIENT',
    stamps: ['readAt'],
    requiresReason: false,
    proposal: false,
    description:
      'First recipient-side open. The originator viewing their own confirmation ' +
      'never advances this state.',
  },
  {
    action: 'reply',
    from: ['READ'],
    to: 'REPLIED',
    permission: 'confirmation.reply',
    actorSide: 'RECIPIENT',
    stamps: ['firstRepliedAt'],
    requiresReason: false,
    proposal: false,
    description: 'Recipient posts the first substantive reply. Stops the SLA response clock.',
  },
  {
    action: 'followUp',
    from: ['REPLIED'],
    to: 'REPLIED',
    permission: 'confirmation.reply',
    actorSide: 'EITHER',
    stamps: ['lastActivityAt'],
    requiresReason: false,
    proposal: false,
    description:
      'Either side continues the thread. Status is unchanged; awaitingParty flips ' +
      'to the other side.',
  },
  {
    action: 'resolve',
    from: ['READ', 'REPLIED'],
    to: 'RESOLVED',
    permission: 'confirmation.resolve',
    actorSide: 'ORIGIN',
    stamps: ['resolvedAt'],
    requiresReason: false,
    proposal: false,
    description: 'Originating side confirms the request has been satisfied (ASM-002).',
  },
  {
    action: 'close',
    from: ['RESOLVED'],
    to: 'CLOSED',
    permission: 'confirmation.close',
    actorSide: 'ORIGIN',
    stamps: ['closedAt'],
    requiresReason: false,
    proposal: false,
    description:
      'Originating manager closes the record, or the auto-close job does so after ' +
      'the configured interval (ASM-003, G-03).',
  },
  {
    action: 'reopen',
    from: ['CLOSED'],
    to: 'REPLIED',
    permission: 'confirmation.reopen',
    actorSide: 'ORIGIN',
    stamps: [],
    requiresReason: true,
    proposal: true,
    description:
      'G-02 — reverses a premature closure without severing the audit chain. ' +
      'Increments reopenedCount, which is reportable as a quality signal.',
  },
  {
    action: 'cancel',
    from: ['SENT', 'RECEIVED', 'READ'],
    to: 'CANCELLED',
    permission: 'confirmation.cancel',
    actorSide: 'ORIGIN',
    stamps: ['cancelledAt'],
    requiresReason: true,
    proposal: true,
    description:
      'G-01 — withdraws a confirmation sent in error, before any reply. Excluded ' +
      'from SLA and response-rate metrics.',
  },
  {
    action: 'discardDraft',
    from: ['DRAFT'],
    to: null,
    permission: 'confirmation.create',
    actorSide: 'ORIGIN',
    stamps: [],
    requiresReason: false,
    proposal: false,
    description: 'Author discards an unsent draft. It never held a reference number.',
  },
  {
    action: 'reassign',
    from: ['RECEIVED', 'READ', 'REPLIED'],
    to: null,
    permission: 'confirmation.reassign',
    actorSide: 'RECIPIENT',
    stamps: [],
    requiresReason: false,
    proposal: true,
    description:
      'G-04 — recipient manager reassigns to another active user in their office. ' +
      'Status is unchanged and the SLA clock does not reset.',
  },
] as const;

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

const BY_ACTION = new Map<ConfirmationAction, TransitionDefinition>(
  TRANSITIONS.map((t) => [t.action, t]),
);

export function transitionFor(action: ConfirmationAction): TransitionDefinition {
  const definition = BY_ACTION.get(action);
  if (definition === undefined) {
    throw new Error(`Unknown confirmation action: ${action}`);
  }
  return definition;
}

/**
 * Whether the state machine permits this action from this status.
 *
 * This is the *structural* check only. Permission, actor side, feature flags
 * and entity-level guards are applied separately by the domain service — a
 * `true` here is necessary, never sufficient.
 */
export function canTransition(from: ConfirmationStatus, action: ConfirmationAction): boolean {
  const definition = BY_ACTION.get(action);
  if (definition === undefined) return false;
  return definition.from.includes(from);
}

/** Every action structurally available from a status. */
export function actionsFrom(status: ConfirmationStatus): readonly TransitionDefinition[] {
  return TRANSITIONS.filter((t) => t.from.includes(status));
}

/** Resulting status, or `undefined` if the transition is not permitted. */
export function nextStatus(
  from: ConfirmationStatus,
  action: ConfirmationAction,
): ConfirmationStatus | null | undefined {
  const definition = BY_ACTION.get(action);
  if (definition === undefined || !definition.from.includes(from)) return undefined;
  return definition.to;
}

/**
 * Error payload for a rejected transition.
 *
 * Rejections are explicit 409s carrying the available alternatives — never a
 * silent no-op, which would leave the caller believing the action succeeded.
 */
export interface InvalidTransition {
  readonly currentStatus: ConfirmationStatus;
  readonly attemptedAction: ConfirmationAction;
  readonly availableActions: readonly ConfirmationAction[];
}

export function describeInvalidTransition(
  currentStatus: ConfirmationStatus,
  attemptedAction: ConfirmationAction,
): InvalidTransition {
  return {
    currentStatus,
    attemptedAction,
    availableActions: actionsFrom(currentStatus).map((t) => t.action),
  };
}
